import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaBuilding,
  FaLocationArrow,
  FaIdCard,
  FaGlobeAsia,
  FaAt,
  FaBarcode,
  FaUniversity,
  FaClipboardList,
  FaTrashAlt,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const initialCustomerState = {
  company_name: '',
  address: '',
  pancard: '',
  city: '',
  state: '',
  country: '',
  gst_number: '',
  gst_uin: '',
  code: '',
  cin: '',
  email: '', // Only used for editing legacy, not for form state
  emails: [''],
};

type CustomerType = Omit<typeof initialCustomerState, 'emails' | 'email'> & {
  id?: string | number,
  emails: string[], // For UI state, but will send as 'email' string to server
};

const inputStyle =
  "flex items-center bg-white rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors shadow-sm w-full";
const labelStyle = "block mb-1 text-sm font-medium text-blue-900 tracking-wide";

const inputInnerStyle =
  "flex-1 outline-none bg-transparent w-full placeholder:truncate";

const AddCustomer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = searchParams.get('customer_id');
  const isEditMode = customerId && customerId !== 'new';

  const [customer, setCustomer] = useState<CustomerType>(initialCustomerState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch existing customer if in edit mode
  useEffect(() => {
    if (isEditMode && customerId) {
      setLoading(true);
      axios
        .get(`${API_URL}/auth/customer/${customerId}`)
        .then((res) => {
          // Normalize to emails array for UI state, but use email string if needed for legacy
          if (Array.isArray(res.data.emails)) {
            setCustomer({ ...res.data, emails: res.data.emails.length > 0 ? res.data.emails : [''] });
          } else if (typeof res.data.email === 'string' && res.data.email.includes(',')) {
            setCustomer({
              ...res.data,
              emails: res.data.email.split(',').map((e: string) => e.trim()).filter(Boolean),
            });
          } else if (typeof res.data.email === 'string') {
            setCustomer({
              ...res.data,
              emails: res.data.email ? [res.data.email] : [''],
            });
          } else if (typeof res.data.emails === 'string') {
            setCustomer({
              ...res.data,
              emails: res.data.emails ? res.data.emails.split(',').map((e: string) => e.trim()).filter(Boolean) : [''],
            });
          } else {
            setCustomer({
              ...res.data,
              emails: [''],
            });
          }
        })
        .catch(() => setError('Unable to load customer details.'))
        .finally(() => setLoading(false));
    } else {
      setCustomer(initialCustomerState);
    }
  }, [customerId, isEditMode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // Only allow individual updating of non-array fields
    if (name === 'emails') return;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  // Email field handlers
  const handleEmailChange = (index: number, value: string) => {
    setCustomer((prev) => {
      const updatedEmails = [...prev.emails];
      updatedEmails[index] = value;
      return { ...prev, emails: updatedEmails };
    });
  };

  const handleAddEmail = () => {
    setCustomer((prev) => ({
      ...prev,
      emails: [...(prev.emails || []), ''],
    }));
  };

  const handleRemoveEmail = (index: number) => {
    setCustomer((prev) => {
      let updatedEmails = [...prev.emails];
      updatedEmails.splice(index, 1);
      // Always keep at least one email field to avoid empty array
      if (updatedEmails.length === 0) updatedEmails = [''];
      return { ...prev, emails: updatedEmails };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccess('');
    if (!customer.company_name.trim()) {
      setFormError('Company name is required.');
      return;
    }
    // Email validation: at least 1 valid non-empty email string
    const cleanedEmails = customer.emails.map(e => e.trim()).filter(Boolean);
    if (cleanedEmails.length === 0) {
      setFormError('At least one email is required.');
      return;
    }

    setSubmitting(true);
    try {
      // Send 'email' as a comma separated string to the server
      const customerToSend = {
        ...customer,
        email: cleanedEmails.join(','),
      };
      // Remove 'emails' key before sending
      delete (customerToSend as any).emails;

      if (isEditMode && customerId) {
        // PUT (update)
        await axios.put(`${API_URL}/auth/customer/${customerId}`, customerToSend);
        setSuccess('Customer updated successfully');
      } else {
        // POST (create)
        await axios.post(`${API_URL}/auth/customer`, customerToSend);
        setSuccess('Customer added successfully');
        // Redirect to customers page after creation
        navigate(`/customer`);
      }
    } catch (e: any) {
      setFormError(e?.response?.data?.error || 'Error saving customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/auth/customer/${customerId}`);
      setShowDeleteConfirm(false);
      setSuccess('Customer deleted successfully.');
      setTimeout(() => navigate('/customers'), 1200);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error deleting customer');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-8 p-8 bg-gradient-to-br from-blue-50 via-pink-50 to-indigo-50 rounded-2xl shadow-xl border border-blue-100 animate__animated animate__fadeIn">
      <h2 className="text-2xl font-bold mb-5 text-blue-900 tracking-wide flex items-center gap-2">
        <FaBuilding className="inline text-blue-500" size={26} />
        {isEditMode ? 'Edit Customer' : 'Add Customer'}
      </h2>
      <div className="mb-3 space-y-2">
        {error && <div className="text-red-700 bg-red-50 border border-red-300 px-4 py-2 rounded">{error}</div>}
        {formError && <div className="text-red-700 bg-red-50 border border-red-300 px-4 py-2 rounded">{formError}</div>}
        {success && <div className="text-green-700 bg-green-50 border border-green-300 px-4 py-2 rounded">{success}</div>}
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Company Name & Emails */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaBuilding className="text-blue-400" /> Company Name <span className="text-red-600">*</span>
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="company_name"
                className={inputInnerStyle}
                value={customer.company_name}
                onChange={handleInputChange}
                required
                autoFocus
                disabled={loading || submitting}
                placeholder="Ex: Acme Pvt Ltd"
                maxLength={64}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaAt className="text-indigo-400" /> Email(s)
              </span>
            </label>
            {/* Render dynamic list of emails */}
            <div className="space-y-2">
              {customer.emails.map((emaildata, idx) => (
                <div key={idx} className={`flex items-center gap-2 mb-1 ${inputStyle}`}>
                  <input
                    type="email"
                    name={`email_${idx}`}
                    className={inputInnerStyle}
                    value={emaildata}
                    onChange={e => handleEmailChange(idx, e.target.value)}
                    disabled={loading || submitting}
                    placeholder="customer@email.com"
                    maxLength={48}
                  />
                  {/* Show remove button only if >1 email fields */}
                  {customer.emails.length > 1 && (
                    <button
                      type="button"
                      title="Remove this email"
                      onClick={() => handleRemoveEmail(idx)}
                      tabIndex={-1}
                      className="ml-1 text-red-500 hover:text-red-700 px-2 py-1 rounded transition"
                      disabled={loading || submitting}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {/* Add email button */}
              <button
                type="button"
                className="mt-1 ml-1 px-3 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 text-sm font-medium transition"
                onClick={handleAddEmail}
                disabled={loading || submitting || customer.emails.length >= 5}
                tabIndex={0}
              >
                + Add Email
              </button>
            </div>
          </div>
        </div>
        {/* Address (full row) */}
        <div>
          <label className={labelStyle}>
            <span className="flex items-center gap-2">
              <FaLocationArrow className="text-pink-400" /> Address
            </span>
          </label>
          <div className={inputStyle}>
            <textarea
              name="address"
              className={inputInnerStyle + " resize-none"}
              value={customer.address}
              onChange={handleInputChange}
              rows={2}
              disabled={loading || submitting}
              placeholder="Full address"
              maxLength={120}
            />
          </div>
        </div>
        {/* PAN, GST Number, GST UIN, CIN - 2 rows, 4 cols */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaIdCard className="text-yellow-500" /> PAN
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="pancard"
                className={inputInnerStyle + " uppercase"}
                value={customer.pancard}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaBarcode className="text-green-500" /> GST Number
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="gst_number"
                className={inputInnerStyle + " uppercase"}
                value={customer.gst_number}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaClipboardList className="text-indigo-400" /> GST UIN
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="gst_uin"
                className={inputInnerStyle}
                value={customer.gst_uin}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="Optional"
                maxLength={16}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaUniversity className="text-teal-500" /> CIN
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="cin"
                className={inputInnerStyle + " uppercase"}
                value={customer.cin}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="L12345MH2012PLC123456"
                maxLength={21}
              />
            </div>
          </div>
        </div>
        {/* City, State, Country, Code - 2 rows, 4 cols */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaLocationArrow className="text-blue-500" /> City
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="city"
                className={inputInnerStyle + " capitalize"}
                value={customer.city}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="City"
                maxLength={32}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaLocationArrow className="text-purple-500" /> State
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="state"
                className={inputInnerStyle + " capitalize"}
                value={customer.state}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="State"
                maxLength={32}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaGlobeAsia className="text-pink-400" /> Country
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="country"
                className={inputInnerStyle + " capitalize"}
                value={customer.country}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="Country"
                maxLength={32}
              />
            </div>
          </div>
          <div>
            <label className={labelStyle}>
              <span className="flex items-center gap-2">
                <FaBarcode className="text-cyan-500" /> Code
              </span>
            </label>
            <div className={inputStyle}>
              <input
                type="text"
                name="code"
                className={inputInnerStyle}
                value={customer.code}
                onChange={handleInputChange}
                disabled={loading || submitting}
                placeholder="Internal code"
                maxLength={16}
              />
            </div>
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex flex-wrap space-x-4 mt-8 items-center">
          <button
            type="submit"
            className={`flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 shadow-lg ${submitting ? 'cursor-not-allowed' : ''}`}
            disabled={submitting || loading}
          >
            {submitting
              ? isEditMode
                ? 'Saving...'
                : 'Adding...'
              : isEditMode
                ? 'Save Changes'
                : 'Add Customer'}
          </button>
          {isEditMode && (
            <>
              <button
                type="button"
                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition shadow"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteLoading}
              >
                <FaTrashAlt className="inline" /> Delete
              </button>
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 transition">
                  <div className="bg-white rounded-xl shadow-2xl p-8 w-[360px] space-y-5 border-2 border-red-200 animate__animated animate__fadeIn">
                    <div className="font-bold text-xl text-red-700 flex gap-2 items-center"><FaTrashAlt/> Delete Customer</div>
                    <div>Are you sure you want to delete this customer?<br/>This will also delete all related bank details.</div>
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-2 rounded-lg border font-semibold"
                        disabled={deleteLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        className={`px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 text-white font-semibold shadow ${deleteLoading ? 'opacity-60' : ''}`}
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddCustomer;